import { useState, useRef, useEffect } from "react";
import { useLocalization } from "@fluent/react";
import { Button, IconButton, Spinner } from "@narmi/design_system";
import styles from "./InvoiceContainer.module.scss";
import ZoomActionBar from "./ZoomActionBar";
import {
  HIGHLIGHT_ZOOM,
  HIGHLIGHT_PAD,
  HIGHLIGHT_BORDER_PX,
  HIGHLIGHT_RADIUS_PX,
  HIGHLIGHT_GLOW_PX,
} from "./constants";
import type { HighlightLocation, InvoicePreviewPage } from "./types";

type PreviewCardProps = {
  /** Local object URL, used before the backend has rendered the invoice. */
  previewUrl: string | null;
  /** Backend-rendered pages, ordered by page number. Wins over previewUrl. */
  previewPages?: InvoicePreviewPage[];
  /** MIME type of the local file. A PDF blob can't be shown in an <img>, so it
   * waits on a spinner until the backend's page renders arrive. */
  previewMimeType?: string | null;
  fileName?: string | null;
  onRemove?: () => void;
  highlight?: HighlightLocation | null;
  showZoomControls?: boolean;
  onZoomInteraction?: () => void;
};

const ZOOM_STEP = 0.25;
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;

const PreviewCard = ({
  previewUrl,
  previewPages,
  previewMimeType,
  fileName,
  onRemove,
  highlight,
  showZoomControls = false,
  onZoomInteraction,
}: PreviewCardProps) => {
  const { l10n } = useLocalization();

  let pages: InvoicePreviewPage[] = [];
  if (previewPages?.length) {
    pages = previewPages;
  } else if (previewUrl) {
    // local blob: dimensions are unknown until the image loads
    pages = [{ url: previewUrl, widthPx: 0, heightPx: 0 }];
  }
  const pageCount = pages.length;
  const isMultiPage = pageCount > 1;
  // only the local blob can be a PDF; backend pages are always JPEG
  const isPdfPreview =
    !previewPages?.length && previewMimeType === "application/pdf";

  // 0-based index into `pages`; highlight.page from the backend is 1-based
  const [activeIndex, setActiveIndex] = useState(0);

  // a shorter document can leave the index past the end (e.g. the member
  // replaces a 3-page invoice with a 1-page one)
  useEffect(() => {
    setActiveIndex((index) => (index > pageCount - 1 ? 0 : index));
  }, [pageCount]);

  // a highlight belongs to a specific page, so bring that page into view
  const highlightPage = highlight?.page;
  useEffect(() => {
    if (highlightPage == null) return;
    const index = highlightPage - 1;
    if (index >= 0 && index < pageCount) {
      setActiveIndex(index);
    }
  }, [highlightPage, pageCount]);

  const activePage = pages[activeIndex] ?? null;
  // only draw the box while its own page is the one on screen
  const rawHighlight =
    highlight && highlight.page === activeIndex + 1 ? highlight : null;

  const activeUrl = activePage?.url ?? null;

  const [naturalRatio, setNaturalRatio] = useState<number | null>(null);

  // the box the card may occupy, measured so the card can be fit to the page
  // ratio along whichever axis runs out first
  const slotRef = useRef<HTMLDivElement>(null);
  const [slot, setSlot] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const node = slotRef.current;
    if (!node) return undefined;
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setSlot({ width, height });
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  let aspectRatio: number | null = null;
  if (naturalRatio) {
    aspectRatio = naturalRatio;
  } else if (activePage?.widthPx && activePage?.heightPx) {
    aspectRatio = activePage.widthPx / activePage.heightPx;
  }

  let activeHighlight: {
    left: number;
    top: number;
    width: number;
    height: number;
  } | null = null;
  if (rawHighlight) {
    const padY = rawHighlight.height * HIGHLIGHT_PAD;
    // same visual gap horizontally, converted into page-width fractions
    const padX = aspectRatio ? padY / aspectRatio : padY;
    const left = Math.max(0, rawHighlight.left - padX);
    const top = Math.max(0, rawHighlight.top - padY);
    activeHighlight = {
      left,
      top,
      width: Math.min(1 - left, rawHighlight.width + padX * 2),
      height: Math.min(1 - top, rawHighlight.height + padY * 2),
    };
  }

  let alt: string;
  if (isMultiPage) {
    alt = l10n.getString("invoice-review-image-alt-page", {
      page: activeIndex + 1,
    });
  } else {
    alt = fileName || l10n.getString("invoice-review-image-alt");
  }

  // a highlight is always shown via the auto-zoom view
  const isHighlighted = activeHighlight != null;

  // manual zoom controlled by the action bar
  const [zoomLevel, setZoomLevel] = useState(1);
  // pan offset (px) applied when the image is zoomed in and dragged around
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  // where the pointer grabbed relative to the current offset
  const dragStart = useRef<{ x: number; y: number } | null>(null);

  // zoom/pan are per-page; carrying them across would drop the member into a
  // random corner of the next page
  useEffect(() => {
    setZoomLevel(1);
    setOffset({ x: 0, y: 0 });
    setNaturalRatio(null);
  }, [activeIndex]);

  // clears the parent's highlight/auto-zoom and returns the image to its
  // normal, un-zoomed state
  const resetHighlight = () => {
    onZoomInteraction?.();
    setZoomLevel(1);
    setOffset({ x: 0, y: 0 });
  };

  // while a highlight is active, the first action-bar click just clears it; the
  // button's normal behavior only kicks in on the next click
  const resetZoom = () => {
    if (isHighlighted) {
      resetHighlight();
      return;
    }
    setZoomLevel(1);
    setOffset({ x: 0, y: 0 });
  };
  const zoomIn = () => {
    if (isHighlighted) {
      resetHighlight();
      return;
    }
    setZoomLevel((level) => Math.min(MAX_ZOOM, level + ZOOM_STEP));
  };
  const zoomOut = () => {
    if (isHighlighted) {
      resetHighlight();
      return;
    }
    setZoomLevel((level) => {
      const next = Math.max(MIN_ZOOM, level - ZOOM_STEP);
      // once we're back to 1x there's nothing to pan, so recenter
      if (next === MIN_ZOOM) {
        setOffset({ x: 0, y: 0 });
      }
      return next;
    });
  };

  // when zoomed to a highlight, scale from the center of the highlight box so it
  // enlarges and shifts that region toward the middle of the viewport
  const zoomOriginX = activeHighlight
    ? (activeHighlight.left + activeHighlight.width / 2) * 100
    : 50;
  const zoomOriginY = activeHighlight
    ? (activeHighlight.top + activeHighlight.height / 2) * 100
    : 50;

  let cardSizingStyle: React.CSSProperties = { width: "100%", height: "100%" };
  if (aspectRatio && slot.width > 0 && slot.height > 0) {
    const width = Math.min(slot.width, slot.height * aspectRatio);
    cardSizingStyle = {
      width: `${width}px`,
      height: `${width / aspectRatio}px`,
    };
  }

  let wrapperStyle: React.CSSProperties | undefined;
  if (isHighlighted) {
    // highlight/auto-zoom view: driven ENTIRELY by the highlight so it always
    // looks identical, regardless of any lingering manual zoom/pan state
    wrapperStyle = {
      transform: `translate(${50 - zoomOriginX}%, ${
        50 - zoomOriginY
      }%) scale(${HIGHLIGHT_ZOOM})`,
      transformOrigin: `${zoomOriginX}% ${zoomOriginY}%`,
    };
  } else if (zoomLevel !== 1) {
    // manual zoom + drag-pan
    wrapperStyle = {
      transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoomLevel})`,
      transformOrigin: "center center",
      // no transition while dragging so the image tracks the cursor 1:1
      transition: isDragging ? "none" : undefined,
    };
  }

  // panning is only meaningful once the image is enlarged past its container
  const canPan = zoomLevel > 1 && !isHighlighted;

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!canPan) return;
    setIsDragging(true);
    dragStart.current = {
      x: event.clientX - offset.x,
      y: event.clientY - offset.y,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging || !dragStart.current) return;
    setOffset({
      x: event.clientX - dragStart.current.x,
      y: event.clientY - dragStart.current.y,
    });
  };

  const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    setIsDragging(false);
    dragStart.current = null;
    event.currentTarget.releasePointerCapture(event.pointerId);
  };

  let cursor: "grab" | "grabbing" | undefined;
  if (canPan) {
    cursor = isDragging ? "grabbing" : "grab";
  }

  return (
    <div className={styles.previewContainer}>
      <div className={styles.previewSlot} ref={slotRef}>
        <div className={styles.previewCard} style={cardSizingStyle}>
          {onRemove && (
            <div className={styles.previewRemoveButton}>
              <IconButton
                name="x-circle"
                textSize="l"
                kind="themed"
                label={l10n.getString("invoice-remove-file")}
                onClick={onRemove}
              />
            </div>
          )}
          {activeUrl && isPdfPreview && (
            <div
              className={styles.previewPlaceholder}
              role="status"
              aria-label={l10n.getString("invoice-preview-preparing")}
            >
              <Spinner size={48} color="var(--color-successDark)" />
            </div>
          )}
          {activeUrl && !isPdfPreview && (
            <div
              className={styles.previewImageWrapper}
              style={{ ...wrapperStyle, cursor }}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
            >
              <img
                className={styles.previewImage}
                src={activeUrl}
                alt={alt}
                draggable={false}
                onLoad={(event) => {
                  const { naturalWidth, naturalHeight } = event.currentTarget;
                  if (naturalWidth && naturalHeight) {
                    setNaturalRatio(naturalWidth / naturalHeight);
                  }
                }}
              />
              {activeHighlight && (
                <div
                  className={styles.highlightBox}
                  style={{
                    left: `${activeHighlight.left * 100}%`,
                    top: `${activeHighlight.top * 100}%`,
                    width: `${activeHighlight.width * 100}%`,
                    height: `${activeHighlight.height * 100}%`,
                    // drawn inside the scaled wrapper, so divide out the zoom to
                    // keep these at their intended on-screen size
                    borderWidth: `${HIGHLIGHT_BORDER_PX / HIGHLIGHT_ZOOM}px`,
                    borderRadius: `${HIGHLIGHT_RADIUS_PX / HIGHLIGHT_ZOOM}px`,
                    // spread-only shadow: extends the fill past the border,
                    // following the same rounded corners
                    boxShadow: `0 0 0 ${
                      HIGHLIGHT_GLOW_PX / HIGHLIGHT_ZOOM
                    }px #ddf6e8`,
                  }}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {isMultiPage && (
        <div className={styles.pageNav}>
          <Button
            kind="plain"
            startIcon="chevron-left"
            ariaLabel={l10n.getString("invoice-preview-page-previous")}
            disabled={activeIndex === 0}
            onClick={() => setActiveIndex((index) => Math.max(0, index - 1))}
          />
          <span
            className={`fontSize--s fontColor--secondary ${styles.pageNavLabel}`}
          >
            {l10n.getString("invoice-preview-page-indicator", {
              current: activeIndex + 1,
              total: pageCount,
            })}
          </span>
          <Button
            kind="plain"
            startIcon="chevron-right"
            ariaLabel={l10n.getString("invoice-preview-page-next")}
            disabled={activeIndex === pageCount - 1}
            onClick={() =>
              setActiveIndex((index) => Math.min(pageCount - 1, index + 1))
            }
          />
        </div>
      )}

      {activeUrl && !isPdfPreview && showZoomControls && (
        <ZoomActionBar
          onReset={resetZoom}
          onZoomIn={zoomIn}
          onZoomOut={zoomOut}
          // while highlighted, both buttons stay enabled so the first click can
          // reset the highlight (zoomLevel is still 1 during a highlight)
          canZoomIn={isHighlighted || zoomLevel < MAX_ZOOM}
          canZoomOut={isHighlighted || zoomLevel > MIN_ZOOM}
        />
      )}
    </div>
  );
};

export default PreviewCard;
